
import React from 'react';
import { Button } from '@/components/ui/button';
import { Bug, Database } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DebugPanelProps {
  realtimeConnected: boolean;
  suggestionsCount: number;
  isLoading: boolean;
  fetchMethod: 'standard' | 'simple' | 'rpc';
  suggestionsPreview: string;
  changeFetchMethod: (method: 'standard' | 'simple' | 'rpc') => void;
  handleCreateTestSuggestion: () => Promise<void>;
  handleCreateDirectSuggestion: () => Promise<void>;
  handleManualCheck: () => Promise<void>;
}

const DebugPanel: React.FC<DebugPanelProps> = ({
  realtimeConnected,
  suggestionsCount,
  isLoading,
  fetchMethod,
  suggestionsPreview,
  changeFetchMethod,
  handleCreateTestSuggestion,
  handleCreateDirectSuggestion,
  handleManualCheck
}) => {
  return (
    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
      <h3 className="font-semibold mb-2">Debug Information</h3>
      <p className="text-sm">Supabase connection: {!!supabase ? 'Connected' : 'Not connected'}</p>
      <p className="text-sm">Realtime subscription: {realtimeConnected ? 'Active' : 'Not active'}</p>
      <p className="text-sm">Suggestions count: {suggestionsCount}</p>
      <p className="text-sm">Loading state: {isLoading ? 'Loading' : 'Not loading'}</p>
      <p className="text-sm">Current fetch method: {fetchMethod}</p>
      
      <div className="mt-2">
        <h4 className="font-medium text-sm mb-1">Try different fetch methods:</h4>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => changeFetchMethod('standard')}
            className={`text-xs ${fetchMethod === 'standard' ? 'bg-blue-100' : ''}`}
          >
            Standard
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => changeFetchMethod('simple')}
            className={`text-xs ${fetchMethod === 'simple' ? 'bg-blue-100' : ''}`}
          >
            Simple
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => changeFetchMethod('rpc')}
            className={`text-xs ${fetchMethod === 'rpc' ? 'bg-blue-100' : ''}`}
          >
            RPC
          </Button>
        </div>
      </div>
      
      <div className="mt-2 space-x-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleCreateTestSuggestion}
          className="flex items-center gap-1 bg-yellow-50"
        >
          <Bug className="w-4 h-4" />
          Test Func
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleCreateDirectSuggestion}
          className="flex items-center gap-1 bg-orange-50"
        >
          <Database className="w-4 h-4" />
          Test Direct
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleManualCheck}
          className="flex items-center gap-1 bg-blue-50"
        >
          <Database className="w-4 h-4" />
          Check DB
        </Button>
      </div>
      
      <p className="text-sm mt-2">Data preview: {suggestionsPreview}</p>
    </div>
  );
};

export default DebugPanel;
